import React, { useState } from 'react';
import ChangeRequestAttachmentList from '../ChangeRequestAttachmentList';
import { DateTimeDisplay } from '@/components/localization';
import theme from './ChangeRequestDetails.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChangeRequest } from '@/types';
import { BatchedChanges, Input, TextArea, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { editChangeRequest } from '@/services/change-requests';
import { ProjectPermissionToggle } from '@/components/permissions';
import { Button, ButtonBar, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';

const ChangeRequestDetailsRequestTab: React.FC<{
    changeRequest: ChangeRequest;
    onChange(changes: BatchedChanges<ChangeRequest>): void;
}> = ({ changeRequest, onChange }) => {
    const [isEdit, setIsEdit] = useState(false);
    const [localChangeRequest, setLocalChangeRequest] = useState<ChangeRequest>(changeRequest);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await editChangeRequest(changeRequest.id, {
                internalDescription: localChangeRequest.internalDescription,
                subject: localChangeRequest.subject,
            });
            onChange(
                BatchedChanges.single(changeRequest.id, {
                    internalDescription: localChangeRequest.internalDescription,
                    subject: localChangeRequest.subject,
                })
            );
            Toast.success(t`Change request edited successfully`);

            setIsEdit(false);
        } catch (e) {
            Toast.error(t`Failed to save changes`);
        } finally {
            setIsSaving(false);
        }
    };

    const renderField = (label: string, value: string, onChange: (e) => void) => (
        <>
            {(value || isEdit) && <h6>{label}</h6>}
            <span>
                {isEdit ? (
                    <TextArea
                        autoResize
                        minRows={3}
                        placeholder={label}
                        className="form-control"
                        value={value}
                        onChange={onChange}
                    />
                ) : (
                    value
                )}
            </span>
        </>
    );

    return (
        <div className={theme.requestDetails}>
            <ProjectPermissionToggle condition={'isAdministrator'}>
                <div className={theme.dropdown}>
                    <DropdownContextMenu>
                        <Dropdown.Item onClick={() => setIsEdit(true)} icon={faPencil}>
                            <Trans>Edit</Trans>
                        </Dropdown.Item>
                    </DropdownContextMenu>
                </div>
            </ProjectPermissionToggle>
            {isEdit && (
                <h6>
                    <Trans>Subject</Trans>
                </h6>
            )}

            <h5>
                {isEdit ? (
                    <Input
                        placeholder={t`Subject`}
                        className="form-control"
                        value={localChangeRequest.subject}
                        onChange={(e) => {
                            setLocalChangeRequest({ ...localChangeRequest, subject: e.target.value });
                        }}
                    />
                ) : (
                    <>
                        {changeRequest.subject}
                        <small>
                            <DateTimeDisplay value={changeRequest.submitDate} />
                        </small>
                    </>
                )}
            </h5>
            {changeRequest.description && (
                <>
                    <h6>
                        <Trans>Description</Trans>
                    </h6>
                    <div className={theme.description}>{changeRequest.description}</div>
                </>
            )}
            {renderField(t`Internal description`, localChangeRequest.internalDescription, (e) =>
                setLocalChangeRequest({ ...localChangeRequest, internalDescription: e.target.value })
            )}
            {isEdit && (
                <ButtonBar>
                    <Button loading={isSaving} onClick={handleSave} variant="primary">
                        <Trans>Save</Trans>
                    </Button>
                    <Button
                        onClick={() => {
                            setIsEdit(false);
                            setLocalChangeRequest(changeRequest);
                        }}
                        variant="tertiary">
                        <Trans>Cancel</Trans>
                    </Button>
                </ButtonBar>
            )}
            {changeRequest.attachments.length > 0 && (
                <>
                    <h6>
                        <Trans>Attachments</Trans>
                    </h6>
                    <ChangeRequestAttachmentList
                        value={changeRequest.attachments}
                        canAdd={false}
                        canEdit={false}
                        showVisibilityFlags={true}
                    />
                </>
            )}
        </div>
    );
};

export default ChangeRequestDetailsRequestTab;
